import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Table } from '../UI';
import { Button } from '../UI/base/Button';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { StaffFormModal } from './StaffFormModal';
import { DeleteStaffModal } from './DeleteStaffModal';
import { getStaffColumns } from './staffColumns';
import { useStaff } from '../../hooks/useStaff';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import type { CreateStaffRequest, StaffResponse } from '../../api/generated';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { getErrorMessage } from '../../utils/errorMessageUtils';
import styles from './StaffSettings.module.css';

export const StaffSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const { staff, isLoading, loadError, locationId, createStaff, updateStaff, deleteStaff } =
    useStaff(routeLocationId ?? null);
  const { competences } = useStaffCompetences();

  const [formOpen, setFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StaffResponse | null>(null);

  const competenceNameById = useMemo(
    () => new Map(competences.map((c) => [c.id, c.name])),
    [competences]
  );

  const columns = useMemo(
    () =>
      getStaffColumns(t, {
        competenceNameById,
        onEdit: (member) => {
          setEditingStaff(member);
          setFormOpen(true);
        },
        onDelete: (member) => setDeleteTarget(member)
      }),
    [t, competenceNameById]
  );

  const handleOpenCreate = () => {
    setEditingStaff(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingStaff(null);
  };

  const handleSave = async (payload: CreateStaffRequest) => {
    const target = editingStaff;
    try {
      if (target) {
        await updateStaff(target.id, payload);
      } else {
        await createStaff(payload);
      }
      handleCloseForm();
    } catch (e) {
      const fallback = target ? t('config-staff-update-error') : t('config-staff-create-error');
      dispatch(showError({ message: getErrorMessage(e, fallback) }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const idToDelete = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await deleteStaff(idToDelete);
    } catch (e) {
      dispatch(showError({ message: getErrorMessage(e, t('config-staff-delete-error')) }));
    }
  };

  const hasNoLocation = !isLoading && !loadError && !locationId;
  const showEmptyState = !isLoading && !loadError && Boolean(locationId) && staff.length === 0;

  return (
    <section>
      <SectionHeader title={t('config-staff-header')}>
        <Button type="primary" disabled={!locationId} onClick={handleOpenCreate}>
          {t('config-staff-add')}
        </Button>
      </SectionHeader>

      {isLoading && staff.length === 0 && (
        <p className={styles.emptyState}>{t('config-staff-loading')}</p>
      )}

      {loadError && <p className={styles.emptyState}>{t('config-staff-load-error')}</p>}

      {hasNoLocation && <p className={styles.emptyState}>{t('config-staff-no-location')}</p>}

      {showEmptyState && <p className={styles.emptyState}>{t('config-staff-empty')}</p>}

      {staff.length > 0 && (
        <Table columns={columns} data={staff} getRowKey={(member) => member.id} minWidth={680} />
      )}

      {formOpen && (
        <StaffFormModal
          staff={editingStaff}
          competences={competences}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}

      {deleteTarget && (
        <DeleteStaffModal
          staffName={deleteTarget.fullName}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
};
